import { useState, useEffect } from "react";
import type { Project } from "@/lib/types";
import { MATERIALS } from "@/lib/types";
import { api } from "@/lib/api";
import { useRouter, Link } from "@/lib/router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StarRating } from "@/components/StarRating";
import {
	GitFork, Download, ArrowLeft, Layers, Thermometer, Gauge,
	Box, ShieldCheck, FileText, Camera, Star, MessageSquare,
	User, Clock, Printer,
} from "lucide-react";
import { cn } from "@/lib/utils";

const materialColors: Record<string, string> = {
	PLA: "bg-emerald-500/15 text-emerald-400",
	ABS: "bg-red-500/15 text-red-400",
	PETG: "bg-blue-500/15 text-blue-400",
	TPU: "bg-purple-500/15 text-purple-400",
	Nylon: "bg-yellow-500/15 text-yellow-400",
	Resin: "bg-pink-500/15 text-pink-400",
	Other: "bg-gray-500/15 text-gray-400",
};

export function ProjectPage({ params }: { params: Record<string, string> }) {
	const { navigate } = useRouter();
	const [project, setProject] = useState<Project | null>(null);
	const [loading, setLoading] = useState(true);
	const [showForkDialog, setShowForkDialog] = useState(false);
	const [showRateDialog, setShowRateDialog] = useState(false);

	// Rate form state
	const [rateRating, setRateRating] = useState(5);
	const [rateComment, setRateComment] = useState("");
	const [rateSubmitting, setRateSubmitting] = useState(false);

	// Fork form state
	const [forkMaterial, setForkMaterial] = useState("");
	const [forkNozzleTemp, setForkNozzleTemp] = useState(0);
	const [forkBedTemp, setForkBedTemp] = useState(0);
	const [forkNotes, setForkNotes] = useState("");
	const [forkSubmitting, setForkSubmitting] = useState(false);

	useEffect(() => {
		setLoading(true);
		api.projects
			.get(params.id)
			.then((p) => {
				setProject(p);
				setForkMaterial(p.printSettings.material);
				setForkNozzleTemp(p.printSettings.nozzleTemp);
				setForkBedTemp(p.printSettings.bedTemp);
				setForkNotes(p.printSettings.notes);
			})
			.finally(() => setLoading(false));
	}, [params.id]);

	async function handleRate() {
		if (!project) return;
		setRateSubmitting(true);
		await api.projects.addResult(project.id, {
			userId: "u1",
			rating: rateRating,
			comment: rateComment,
			photoUrls: [],
		});
		const updated = await api.projects.get(project.id);
		setProject(updated);
		setRateComment("");
		setRateRating(5);
		setShowRateDialog(false);
		setRateSubmitting(false);
	}

	async function handleFork() {
		if (!project) return;
		setForkSubmitting(true);
		const forked = await api.projects.fork(project.id, "u1", {
			material: forkMaterial,
			nozzleTemp: forkNozzleTemp,
			bedTemp: forkBedTemp,
			notes: forkNotes,
		});
		setForkSubmitting(false);
		setShowForkDialog(false);
		navigate(`/project/${forked.id}`);
	}

	if (loading) {
		return (
			<div className="space-y-4 animate-pulse">
				<div className="h-8 bg-muted/50 rounded w-1/3" />
				<div className="h-64 bg-muted/30 rounded-xl" />
				<div className="h-32 bg-muted/30 rounded-xl" />
			</div>
		);
	}

	if (!project) {
		return (
			<div className="text-center py-16">
				<Box className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
				<p className="text-muted-foreground">Project not found</p>
				<Button variant="ghost" onClick={() => navigate("/")} className="mt-4">
					<ArrowLeft className="h-4 w-4 mr-2" /> Back to Browse
				</Button>
			</div>
		);
	}

	const ps = project.printSettings;

	return (
		<div className="space-y-6">
			{/* Back link */}
			<button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
				<ArrowLeft className="h-4 w-4" />
				Back to Browse
			</button>

			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-start gap-4">
				<div className="flex-1 min-w-0 space-y-2">
					<div className="flex items-center gap-2 flex-wrap">
						<h1 className="text-2xl font-bold tracking-tight">{project.title}</h1>
						<Badge className={cn("text-xs font-semibold", materialColors[ps.material] || materialColors.Other)}>
							{ps.material}
						</Badge>
					</div>

					{project.forkedFrom && (
						<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<GitFork className="h-3.5 w-3.5" />
							Forked from{" "}
							<Link to={`/project/${project.forkedFrom.id}`} className="text-orange-400 hover:underline">
								{project.forkedFrom.title}
							</Link>{" "}
							by {project.forkedFrom.creator}
						</div>
					)}

					<p className="text-sm text-muted-foreground">{project.description}</p>

					<div className="flex items-center gap-4 text-sm text-muted-foreground">
						<Link to={`/project/${project.id}`} className="flex items-center gap-1.5 hover:text-foreground transition-colors">
							<div className="h-6 w-6 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-[10px] font-bold text-white">
								{project.creator.displayName[0]}
							</div>
							{project.creator.displayName}
						</Link>
						<span className="flex items-center gap-1">
							<Clock className="h-3.5 w-3.5" />
							{new Date(project.createdAt).toLocaleDateString()}
						</span>
					</div>

					{/* Tags */}
					<div className="flex flex-wrap gap-1.5">
						{project.tags.map((tag) => (
							<Badge key={tag} variant="outline" className="text-[11px]">
								{tag}
							</Badge>
						))}
					</div>
				</div>

				{/* Action buttons */}
				<div className="flex flex-col gap-2 sm:items-end shrink-0">
					<div className="flex gap-2">
						<Dialog open={showForkDialog} onOpenChange={setShowForkDialog}>
							<DialogTrigger asChild>
								<Button variant="outline" size="sm" className="gap-1.5">
									<GitFork className="h-4 w-4" />
									Fork Config
									{project.forkCount > 0 && (
										<span className="text-muted-foreground ml-1">({project.forkCount})</span>
									)}
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Fork Print Configuration</DialogTitle>
								</DialogHeader>
								<div className="space-y-4 pt-2">
									<p className="text-sm text-muted-foreground">
										Create a remix with your own settings based on "{project.title}"
									</p>
									<div className="grid grid-cols-2 gap-3">
										<div>
											<Label className="text-xs">Material</Label>
											<select
												value={forkMaterial}
												onChange={(e) => setForkMaterial(e.target.value)}
												className="w-full mt-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
											>
												{MATERIALS.map((m) => (
													<option key={m} value={m}>{m}</option>
												))}
											</select>
										</div>
										<div>
											<Label className="text-xs">Nozzle Temp (°C)</Label>
											<Input
												type="number"
												value={forkNozzleTemp}
												onChange={(e) => setForkNozzleTemp(Number(e.target.value))}
												className="mt-1"
											/>
										</div>
										<div>
											<Label className="text-xs">Bed Temp (°C)</Label>
											<Input
												type="number"
												value={forkBedTemp}
												onChange={(e) => setForkBedTemp(Number(e.target.value))}
												className="mt-1"
											/>
										</div>
									</div>
									<div>
										<Label className="text-xs">Notes</Label>
										<Textarea
											value={forkNotes}
											onChange={(e) => setForkNotes(e.target.value)}
											className="mt-1"
											rows={3}
										/>
									</div>
									<Button onClick={handleFork} disabled={forkSubmitting} className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700">
										{forkSubmitting ? "Forking..." : "Create Fork"}
									</Button>
								</div>
							</DialogContent>
						</Dialog>

						<Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
							<DialogTrigger asChild>
								<Button size="sm" className="gap-1.5 bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700">
									<Printer className="h-4 w-4" />
									Rate Print
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Rate This Print</DialogTitle>
								</DialogHeader>
								<div className="space-y-4 pt-2">
									<p className="text-sm text-muted-foreground">
										Share your experience printing "{project.title}"
									</p>
									<div>
										<Label className="text-xs">Reliability Rating</Label>
										<div className="mt-2">
											<StarRating
												rating={rateRating}
												interactive
												onRate={setRateRating}
												size="lg"
											/>
										</div>
									</div>
									<div>
										<Label className="text-xs">Your Experience</Label>
										<Textarea
											value={rateComment}
											onChange={(e) => setRateComment(e.target.value)}
											placeholder="How did the print go? Any tips?"
											className="mt-1"
											rows={3}
										/>
									</div>
									<Button onClick={handleRate} disabled={rateSubmitting || !rateComment.trim()} className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700">
										{rateSubmitting ? "Submitting..." : "Submit Rating"}
									</Button>
								</div>
							</DialogContent>
						</Dialog>
					</div>

					{project.ratingCount > 0 && (
						<div className="flex items-center gap-2">
							<StarRating rating={project.averageRating} size="sm" />
							<span className="text-sm font-medium">{project.averageRating.toFixed(1)}</span>
							<span className="text-xs text-muted-foreground">({project.ratingCount} ratings)</span>
						</div>
					)}
				</div>
			</div>

			{/* Main Content */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Left: Preview + Tabs */}
				<div className="lg:col-span-2 space-y-4">
					{/* STL Preview area */}
					<Card className="overflow-hidden border-border/50 bg-card/50">
						<div className="aspect-video bg-gradient-to-br from-muted/60 to-muted flex items-center justify-center">
							<div className="flex flex-col items-center gap-3 text-muted-foreground/40">
								<Box className="h-16 w-16" strokeWidth={1} />
								<span className="text-sm font-medium uppercase tracking-wider">3D Model Preview</span>
								<span className="text-xs">{project.stlFileName} ({(project.stlFileSize / 1000000).toFixed(1)} MB)</span>
							</div>
						</div>
					</Card>

					{/* Tabs */}
					<Tabs defaultValue="results" className="w-full">
						<TabsList className="w-full justify-start bg-card/50 border border-border/50">
							<TabsTrigger value="results" className="gap-1.5 text-xs">
								<MessageSquare className="h-3.5 w-3.5" />
								Print Results ({project.printResults.length})
							</TabsTrigger>
							<TabsTrigger value="settings" className="gap-1.5 text-xs">
								<FileText className="h-3.5 w-3.5" />
								Full Settings
							</TabsTrigger>
						</TabsList>

						<TabsContent value="results" className="space-y-3 mt-4">
							{project.printResults.length === 0 ? (
								<Card className="border-border/50 bg-card/50">
									<CardContent className="py-8 text-center">
										<Camera className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
										<p className="text-sm text-muted-foreground">No print results yet</p>
										<p className="text-xs text-muted-foreground/60 mt-1">Be the first to share your print!</p>
									</CardContent>
								</Card>
							) : (
								project.printResults.map((result) => (
									<Card key={result.id} className="border-border/50 bg-card/50">
										<CardContent className="p-4">
											<div className="flex items-start gap-3">
												<div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-xs font-bold text-white shrink-0">
													{result.user.displayName[0]}
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-center justify-between gap-2">
														<span className="text-sm font-medium">{result.user.displayName}</span>
														<div className="flex items-center gap-2">
															<StarRating rating={result.rating} size="sm" />
															<span className="text-xs text-muted-foreground">
																{new Date(result.createdAt).toLocaleDateString()}
															</span>
														</div>
													</div>
													<p className="text-sm text-muted-foreground mt-1">{result.comment}</p>
												</div>
											</div>
										</CardContent>
									</Card>
								))
							)}
						</TabsContent>

						<TabsContent value="settings" className="mt-4">
							<Card className="border-border/50 bg-card/50">
								<CardContent className="p-4">
									<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
										<SettingItem icon={Box} label="Material" value={ps.material} />
										<SettingItem icon={Thermometer} label="Nozzle Temp" value={`${ps.nozzleTemp}°C`} />
										<SettingItem icon={Thermometer} label="Bed Temp" value={`${ps.bedTemp}°C`} />
										<SettingItem icon={Layers} label="Layer Height" value={`${ps.layerHeight}mm`} />
										<SettingItem icon={Gauge} label="Infill" value={`${ps.infillPercent}%`} />
										<SettingItem icon={Gauge} label="Speed" value={`${ps.printSpeed}mm/s`} />
										<SettingItem icon={ShieldCheck} label="Supports" value={ps.supports ? "Yes" : "No"} />
									</div>
									{ps.notes && (
										<>
											<Separator className="my-4" />
											<div>
												<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</span>
												<p className="text-sm mt-1">{ps.notes}</p>
											</div>
										</>
									)}
								</CardContent>
							</Card>
						</TabsContent>
					</Tabs>
				</div>

				{/* Right Sidebar */}
				<div className="space-y-4">
					{/* Print Settings Quick Card */}
					<Card className="border-border/50 bg-card/50">
						<CardHeader className="pb-3">
							<CardTitle className="text-sm flex items-center gap-2">
								<Gauge className="h-4 w-4 text-orange-400" />
								Print Settings
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2.5">
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Material</span>
								<Badge className={cn("text-[10px]", materialColors[ps.material] || materialColors.Other)}>
									{ps.material}
								</Badge>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Nozzle</span>
								<span className="font-medium">{ps.nozzleTemp}°C</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Bed</span>
								<span className="font-medium">{ps.bedTemp}°C</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Layer Height</span>
								<span className="font-medium">{ps.layerHeight}mm</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Infill</span>
								<span className="font-medium">{ps.infillPercent}%</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Speed</span>
								<span className="font-medium">{ps.printSpeed}mm/s</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Supports</span>
								<span className="font-medium">{ps.supports ? "Required" : "None"}</span>
							</div>
						</CardContent>
					</Card>

					{/* Creator Card */}
					<Card className="border-border/50 bg-card/50">
						<CardHeader className="pb-3">
							<CardTitle className="text-sm flex items-center gap-2">
								<User className="h-4 w-4 text-orange-400" />
								Creator
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="flex items-center gap-3">
								<div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-sm font-bold text-white">
									{project.creator.displayName[0]}
								</div>
								<div>
									<p className="text-sm font-medium">{project.creator.displayName}</p>
									<p className="text-xs text-muted-foreground">
										{project.creator.projectCount} projects &middot; Rep {project.creator.reputation}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Stats Card */}
					<Card className="border-border/50 bg-card/50">
						<CardHeader className="pb-3">
							<CardTitle className="text-sm flex items-center gap-2">
								<Star className="h-4 w-4 text-orange-400" />
								Stats
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Rating</span>
								<span className="font-medium">
									{project.averageRating > 0
										? `${project.averageRating.toFixed(1)} / 5`
										: "No ratings"}
								</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Print Results</span>
								<span className="font-medium">{project.printResults.length}</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">Forks</span>
								<span className="font-medium">{project.forkCount}</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-muted-foreground">File Size</span>
								<span className="font-medium">{(project.stlFileSize / 1000000).toFixed(1)} MB</span>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}

function SettingItem({
	icon: Icon,
	label,
	value,
}: {
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	value: string;
}) {
	return (
		<div className="flex items-center gap-2">
			<div className="h-8 w-8 rounded-lg bg-muted/50 flex items-center justify-center">
				<Icon className="h-4 w-4 text-muted-foreground" />
			</div>
			<div>
				<p className="text-[11px] text-muted-foreground">{label}</p>
				<p className="text-sm font-medium">{value}</p>
			</div>
		</div>
	);
}
